import { logger } from "@octopusdeploy/logging";
import { AnyInterruptionRequiresUserInteraction, TaskState, type InterruptionType } from "@octopusdeploy/octopus-server-client";
import cancelledFavicon from "~/public/images/favicon-cancelled.png";
import cancellingFavicon from "~/public/images/favicon-cancelling.png";
import failedFavicon from "~/public/images/favicon-failure.png";
import runningFavicon from "~/public/images/favicon-in-progress.png";
import manualInterventionFavicon from "~/public/images/favicon-manual-intervention.png";
import queuedFavicon from "~/public/images/favicon-queued.png";
import successFavicon from "~/public/images/favicon-success.png";
import timedoutFavicon from "~/public/images/favicon-timed-out.png";
import defaultFavicon from "~/public/images/favicon.png";

export interface FaviconStatus {
    running: string;
    success: string;
    failed: string;
    timedOut: string;
    cancelled: string;
    cancelling: string;
    manualIntervention: string;
    queued: string;
    default: string;
}

const faviconUrls: FaviconStatus = {
    running: runningFavicon,
    success: successFavicon,
    failed: failedFavicon,
    timedOut: timedoutFavicon,
    cancelled: cancelledFavicon,
    cancelling: cancellingFavicon,
    manualIntervention: manualInterventionFavicon,
    queued: queuedFavicon,
    default: defaultFavicon,
};

const taskStateIconMap: Record<TaskState, string> = {
    [TaskState.Executing]: faviconUrls.running,
    [TaskState.Queued]: faviconUrls.queued,
    [TaskState.Cancelling]: faviconUrls.cancelling,
    [TaskState.Failed]: faviconUrls.failed,
    [TaskState.Canceled]: faviconUrls.cancelled,
    [TaskState.TimedOut]: faviconUrls.timedOut,
    [TaskState.Success]: faviconUrls.success,
};

let originalFavicon: string | null = null;
let currentFaviconElement: HTMLLinkElement | null = null;

function captureOriginalFavicon(): void {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!existingFavicon) {
        logger.warn("Expected a favicon element but none was found. This could indicate error in the build process.");
        return;
    }

    originalFavicon = existingFavicon.href;
    currentFaviconElement = existingFavicon;
}

function setFavicon(currentFaviconElement: HTMLLinkElement | null, url: string): void {
    if (!currentFaviconElement) return;

    currentFaviconElement.href = url;
}

export function updateFavicon(taskState: TaskState, isCompleted: boolean, pendingInterruptionTypes: InterruptionType[]): void {
    if (originalFavicon === null) {
        captureOriginalFavicon();
    }

    let faviconUrl: string = taskStateIconMap[taskState];

    if (AnyInterruptionRequiresUserInteraction(pendingInterruptionTypes)) {
        faviconUrl = faviconUrls.manualIntervention;
    } else if (TaskState.Success && !isCompleted) {
        faviconUrl = faviconUrls.running;
    }

    setFavicon(currentFaviconElement, faviconUrl);
}

export function restoreOriginalFavicon(): void {
    if (originalFavicon && currentFaviconElement) {
        currentFaviconElement.href = originalFavicon;
    }
}

export const faviconManager = {
    updateFavicon,
    restoreOriginalFavicon,
};
